import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import {
  FiMail,
  FiRefreshCw,
  FiSend,
  FiCheckCircle,
  FiActivity,
  FiAlertCircle,
  FiSettings,
} from 'react-icons/fi';
import LoadingButton, { ButtonSpinner } from '../LoadingButton';
import QueryLoadStatus, { QueryLoadingPanel } from '../QueryLoadStatus';
import { actionLabel, emailStatusClass, formatEmailDate } from './emailUtils';
import {
  fetchEmailQueueEntries,
  fetchEmailQueueStatus,
  mergeEmailQueueView,
  postEmailQueueTest,
} from './emailApi';
import { appAlert } from '../../utils/appDialog';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

const QUEUE_QUERY_OPTS = {
  keepPreviousData: true,
  refetchOnWindowFocus: true,
  retry: 1,
  staleTime: 15_000,
  refetchInterval: 30_000,
};

function QueueDetailCard({ title, entry, icon: Icon, variant = 'default' }) {
  return (
    <div className={`email-detail-card email-detail-card--${variant}`}>
      <div className="email-detail-card-head">
        {Icon && <Icon aria-hidden />}
        <h3>{title}</h3>
      </div>
      {!entry ? (
        <p className="email-detail-empty">No entries yet.</p>
      ) : (
        <dl className="email-detail-list">
          <div>
            <dt>Queue ID</dt>
            <dd>#{entry.id}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`email-status-pill email-status-pill--${emailStatusClass(entry.status)}`}>
                {entry.status}
              </span>
            </dd>
          </div>
          <div>
            <dt>Action</dt>
            <dd>{actionLabel(entry.action_id)}</dd>
          </div>
          <div>
            <dt>Recipient</dt>
            <dd>{entry.recipient}</dd>
          </div>
          <div className="email-detail-span">
            <dt>Subject</dt>
            <dd>{entry.subject || '—'}</dd>
          </div>
          <div>
            <dt>Queued</dt>
            <dd>{formatEmailDate(entry.created_at)}</dd>
          </div>
          <div>
            <dt>Sent</dt>
            <dd>{formatEmailDate(entry.sent_at)}</dd>
          </div>
          {entry.skip_reason && (
            <div className="email-detail-span">
              <dt>Skip reason</dt>
              <dd>{entry.skip_reason}</dd>
            </div>
          )}
          {entry.error_message && (
            <div className="email-detail-span">
              <dt>Error</dt>
              <dd className="email-error-text">{entry.error_message}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

const EmailQueueDashboard = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [testTo, setTestTo] = useState(user?.email || '');
  const canLoadEmailConfig = hasPermission('email_manage');

  const { data: status, isLoading: statusLoading, isError: statusError, error: statusErr, isFetching: statusFetching } = useQuery(
    ['email-queue-status'],
    fetchEmailQueueStatus,
    QUEUE_QUERY_OPTS
  );

  const { data: emailConfig } = useQuery(
    ['settings-email'],
    () => api.get('/permissions/email').then((r) => r.data),
    {
      enabled: canLoadEmailConfig,
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: entries = [],
    isLoading: entriesLoading,
    isError: entriesError,
    error: entriesErr,
    isFetching: entriesFetching,
  } = useQuery(
    ['email-queue'],
    () => fetchEmailQueueEntries(50),
    QUEUE_QUERY_OPTS
  );

  const {
    pageItems: pagedEntries,
    pagination: emailPagination,
    setPage: setEmailPage,
  } = useClientPager(entries);

  const view = useMemo(() => mergeEmailQueueView(status, emailConfig), [status, emailConfig]);

  const testMutation = useMutation(
    (to) => postEmailQueueTest(to),
    {
      onSuccess: (res) => {
        void appAlert(res.data?.message || 'Test email sent.');
        queryClient.invalidateQueries(['email-queue-status']);
        queryClient.invalidateQueries(['email-queue']);
        if (canLoadEmailConfig) {
          queryClient.invalidateQueries(['settings-email']);
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.response?.data?.details || e.message),
    }
  );

  const refreshAll = () => {
    queryClient.invalidateQueries(['email-queue-status']);
    queryClient.invalidateQueries(['email-queue']);
    if (canLoadEmailConfig) {
      queryClient.invalidateQueries(['settings-email']);
    }
  };

  if (statusLoading && !status && !(canLoadEmailConfig && emailConfig)) {
    return (
      <QueryLoadingPanel
        isLoading={statusLoading}
        isFetching={statusFetching}
        hasData={false}
        initialLabel="Loading email queue…"
      />
    );
  }

  const counts = view.counts || {};
  const canSendTest = view.smtp_configured;
  const queueError = statusError || entriesError;
  const queueErrorMsg =
    statusErr?.response?.data?.error ||
    statusErr?.response?.data?.details ||
    entriesErr?.response?.data?.error ||
    entriesErr?.message;

  return (
    <div className="email-queue-dashboard" aria-busy={(statusFetching || entriesFetching) && Boolean(status)}>
      <p className="email-queue-intro">
        Monitor outbound email delivery. SMTP and templates are configured in{' '}
        <Link to="/settings?tab=email" className="email-inline-link">
          Settings → Email configuration
        </Link>
        .
      </p>

      {queueError && (
        <div className="email-queue-error" role="alert">
          <FiAlertCircle aria-hidden />
          <span>{queueErrorMsg || 'Could not load email queue data.'}</span>
        </div>
      )}

      <div className="email-status-grid">
        <div className={`email-status-card${view.enabled ? ' is-ok' : ''}`}>
          <FiMail aria-hidden />
          <div>
            <span className="email-status-card-label">Outbound email</span>
            <strong>{view.enabled ? 'Enabled' : 'Disabled'}</strong>
          </div>
        </div>
        <div className={`email-status-card${view.smtp_configured ? ' is-ok' : ' is-error'}`}>
          <FiSettings aria-hidden />
          <div>
            <span className="email-status-card-label">SMTP</span>
            <strong>{view.smtp_configured ? 'Configured' : 'Not configured'}</strong>
          </div>
        </div>
        <div className="email-status-card">
          <FiCheckCircle aria-hidden />
          <div>
            <span className="email-status-card-label">Sent (last 30 days)</span>
            <strong>{counts.sent ?? 0}</strong>
          </div>
        </div>
        <div className="email-status-card">
          <FiActivity aria-hidden />
          <div>
            <span className="email-status-card-label">Last 24 hours</span>
            <strong>{counts.last_24h ?? 0}</strong>
          </div>
        </div>
        <div className="email-status-card">
          <FiAlertCircle aria-hidden />
          <div>
            <span className="email-status-card-label">Failed / skipped</span>
            <strong>{(counts.failed ?? 0) + (counts.skipped ?? 0)}</strong>
          </div>
        </div>
        <div className="email-status-card">
          <FiRefreshCw aria-hidden />
          <div>
            <span className="email-status-card-label">Pending</span>
            <strong>{counts.pending ?? 0}</strong>
          </div>
        </div>
      </div>

      <div className="email-toolbar">
        <button type="button" className="btn btn-secondary" onClick={refreshAll}>
          {statusFetching || entriesFetching ? <ButtonSpinner /> : <FiRefreshCw />} Refresh
        </button>
        <QueryLoadStatus
          isLoading={statusLoading || entriesLoading}
          isFetching={statusFetching || entriesFetching}
          hasData={Boolean(status) || entries.length > 0}
        />
        <div className="email-test-inline">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            aria-label="Test email recipient"
          />
          <LoadingButton
            type="button"
            className="btn btn-primary"
            icon={FiSend}
            loading={testMutation.isLoading}
            loadingLabel="Sending…"
            onClick={() => testMutation.mutate(testTo)}
            disabled={!canSendTest}
          >
            Send test
          </LoadingButton>
        </div>
        {!canSendTest && (
          <span className="email-toolbar-hint">
            <FiAlertCircle aria-hidden /> Configure SMTP in settings before sending a test email.
          </span>
        )}
        {canSendTest && !view.enabled && (
          <span className="email-toolbar-hint">
            Test sends work while outbound email is disabled; only configured notification actions send in production.
          </span>
        )}
      </div>

      <div className="email-run-cards">
        <QueueDetailCard title="Last queue entry" entry={view.last_entry} icon={FiActivity} />
        <QueueDetailCard
          title="Last successful send"
          entry={view.last_successful}
          icon={FiCheckCircle}
          variant="success"
        />
      </div>

      <section className="email-queue-section">
        <h2>Queue history (last 30 days)</h2>
        {entriesLoading && entries.length === 0 ? (
          <QueryLoadingPanel
            isLoading={entriesLoading}
            isFetching={entriesFetching}
            hasData={false}
            initialLabel="Loading queue…"
          />
        ) : entries.length === 0 ? (
          <p className="email-detail-empty">No emails in the queue for the last 30 days.</p>
        ) : (
          <div className="email-queue-table-wrap">
            <table className="data-table email-queue-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Source</th>
                  <th>Queued</th>
                  <th>Sent</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {pagedEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>#{entry.id}</td>
                    <td>
                      <span className={`email-status-pill email-status-pill--${emailStatusClass(entry.status)}`}>
                        {entry.status}
                      </span>
                    </td>
                    <td>{actionLabel(entry.action_id)}</td>
                    <td>{entry.recipient}</td>
                    <td className="email-subject-cell">{entry.subject || '—'}</td>
                    <td>{entry.trigger_source || '—'}</td>
                    <td>{formatEmailDate(entry.created_at)}</td>
                    <td>{formatEmailDate(entry.sent_at)}</td>
                    <td className="email-error-cell">
                      {entry.error_message || entry.skip_reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ListPager
          page={emailPagination.page}
          pageCount={emailPagination.pageCount}
          total={emailPagination.total}
          limit={emailPagination.limit}
          onPageChange={setEmailPage}
        />
      </section>
    </div>
  );
};

export default EmailQueueDashboard;
